import { SocialLink, SocialPlatform } from "../types/outreach";

interface SocialPlatformMetadata {
  value: SocialPlatform;
  label: string;
  legacySuffix?: string;
  buildUrl?: (handle: string) => string;
}

const buildHttpsUrl = (value: string) => {
  if (!value) {
    return "";
  }
  if (/^https?:\/\//i.test(value)) {
    return value;
  }
  return `https://${value}`;
};

export const SOCIAL_PLATFORM_ORDER: SocialPlatform[] = [
  "instagram",
  "twitter",
  "youtube",
  "tiktok",
  "facebook",
  "website",
  "other",
];

export const SOCIAL_PLATFORM_METADATA: Record<SocialPlatform, SocialPlatformMetadata> = {
  instagram: {
    value: "instagram",
    label: "Instagram",
    legacySuffix: "IG",
    buildUrl: (handle) => `https://instagram.com/${handle}`,
  },
  twitter: {
    value: "twitter",
    label: "X (Twitter)",
    legacySuffix: "TW",
    buildUrl: (handle) => `https://twitter.com/${handle}`,
  },
  youtube: {
    value: "youtube",
    label: "YouTube",
    legacySuffix: "YT",
    buildUrl: (handle) => {
      if (!handle) {
        return "";
      }
      if (handle.startsWith("http")) {
        return handle;
      }
      if (handle.startsWith("channel/") || handle.startsWith("c/") || handle.startsWith("user/")) {
        return `https://www.youtube.com/${handle}`;
      }
      const normalized = handle.startsWith("@") ? handle : `@${handle}`;
      return `https://www.youtube.com/${normalized}`;
    },
  },
  tiktok: {
    value: "tiktok",
    label: "TikTok",
    legacySuffix: "TT",
    buildUrl: (handle) => `https://www.tiktok.com/@${handle}`,
  },
  facebook: {
    value: "facebook",
    label: "Facebook",
    legacySuffix: "FB",
    buildUrl: (handle) => `https://facebook.com/${handle}`,
  },
  website: {
    value: "website",
    label: "Website",
  },
  other: {
    value: "other",
    label: "Other",
  },
};

const LEGACY_TO_PLATFORM: Record<string, SocialPlatform> = Object.values(
  SOCIAL_PLATFORM_METADATA
).reduce((acc, metadata) => {
  if (metadata.legacySuffix) {
    acc[metadata.legacySuffix] = metadata.value;
  }
  return acc;
}, {} as Record<string, SocialPlatform>);

const sanitizeHandle = (platform: SocialPlatform, value: string): string => {
  let handle = value.trim();
  if (!handle) {
    return "";
  }

  if (/^https?:\/\//i.test(handle)) {
    try {
      const url = new URL(handle);
      const pathname = url.pathname.replace(/\/+$/, "");
      const segments = pathname.split("/").filter(Boolean);
      switch (platform) {
        case "instagram":
        case "twitter":
        case "tiktok":
        case "facebook":
          handle = segments[segments.length - 1] ?? "";
          break;
        case "youtube":
          if (pathname.startsWith("/@")) {
            handle = pathname.slice(1);
          } else if (segments.length > 1) {
            handle = `${segments[segments.length - 2]}/${segments[segments.length - 1]}`;
          } else if (segments.length === 1) {
            handle = segments[0];
          } else {
            handle = url.hostname;
          }
          break;
        case "website":
        case "other":
          handle = `${url.hostname}${pathname ? pathname : ""}`;
          break;
        default:
          handle = segments[segments.length - 1] ?? handle;
      }
    } catch {
      // Ignore URL parsing errors and fall back to raw value
    }
  }

  switch (platform) {
    case "instagram":
    case "twitter":
    case "tiktok":
    case "facebook":
      handle = handle.replace(/^@/, "");
      handle = handle.split(/[/?#]/)[0] ?? handle;
      break;
    case "youtube":
      handle = handle.replace(/^https?:\/\//i, "");
      handle = handle.replace(/^www\./i, "");
      if (handle.startsWith("youtube.com/")) {
        handle = handle.replace(/^youtube\.com\//i, "");
      }
      if (!handle.startsWith("@") && !handle.startsWith("channel/") && !handle.startsWith("c/") && !handle.startsWith("user/")) {
        handle = handle.replace(/^@/, "");
        handle = handle.split(/[/?#]/)[0] ?? handle;
        handle = `@${handle}`;
      }
      break;
    case "website":
      handle = handle.replace(/^https?:\/\//i, "");
      handle = handle.replace(/^www\./i, "");
      handle = handle.replace(/\/+$/, "");
      break;
    case "other":
      handle = handle.trim();
      break;
    default:
      break;
  }

  return handle;
};

const buildUrlFromHandle = (
  platform: SocialPlatform,
  handle: string,
  rawInput?: string
): string | undefined => {
  if (!handle) {
    return rawInput && /^https?:\/\//i.test(rawInput) ? rawInput : undefined;
  }

  const metadata = SOCIAL_PLATFORM_METADATA[platform];
  if (platform === "website") {
    if (rawInput && /^https?:\/\//i.test(rawInput)) {
      return rawInput;
    }
    if (/^https?:\/\//i.test(handle)) {
      return handle;
    }
    return buildHttpsUrl(handle);
  }

  if (metadata.buildUrl) {
    return metadata.buildUrl(handle.replace(/^@/, ""));
  }

  if (rawInput && /^https?:\/\//i.test(rawInput)) {
    return rawInput;
  }

  return undefined;
};

const buildLabel = (
  platform: SocialPlatform,
  handle: string,
  url?: string
): string => {
  const metadata = SOCIAL_PLATFORM_METADATA[platform];
  if (!handle && url) {
    return url.replace(/^https?:\/\//i, "");
  }

  switch (platform) {
    case "instagram":
    case "twitter":
    case "tiktok":
      return `@${handle.replace(/^@/, "")}`;
    case "youtube":
      if (handle.startsWith("@")) {
        return handle;
      }
      if (handle.startsWith("channel/")) {
        return `youtube.com/${handle}`;
      }
      return `@${handle.replace(/^@/, "")}`;
    case "facebook":
      return handle ? `facebook.com/${handle}` : metadata.label;
    case "website":
      return handle || (url ? url.replace(/^https?:\/\//i, "") : metadata.label);
    default:
      return handle || url || metadata.label;
  }
};

export const normalizeSocialLinkInput = (
  platform: SocialPlatform,
  value: string
): SocialLink => {
  const raw = value.trim();
  if (!raw) {
    throw new Error("Cannot normalize empty social link value");
  }

  const handle = sanitizeHandle(platform, raw);
  const url = buildUrlFromHandle(platform, handle, raw);
  const label = buildLabel(platform, handle, url);

  return {
    platform,
    handle,
    url,
    label,
  };
};

export const normalizeExistingSocialLink = (link: SocialLink): SocialLink => {
  try {
    const value = link.url || link.label || link.handle;
    if (!value) {
      return link;
    }
    return normalizeSocialLinkInput(link.platform, value);
  } catch {
    return link;
  }
};

export const normalizeSocialLinksArray = (
  links?: SocialLink[] | null
): SocialLink[] => {
  if (!links || links.length === 0) {
    return [];
  }

  const normalized = links
    .map((link) => normalizeExistingSocialLink(link))
    .filter((link): link is SocialLink => Boolean(link.handle || link.url));

  const seen = new Set<string>();
  const deduped: SocialLink[] = [];
  for (const link of normalized) {
    const key = `${link.platform}:${link.handle.toLowerCase()}`;
    if (!seen.has(key)) {
      seen.add(key);
      deduped.push(link);
    }
  }

  return sortSocialLinks(deduped);
};

export const parseLegacySocialHandles = (legacy: string): SocialLink[] => {
  if (!legacy) {
    return [];
  }

  const entries = legacy
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);

  const links: SocialLink[] = [];
  for (const entry of entries) {
    const match = entry.match(/^(.*?)(?:\(([^)]+)\))?$/);
    if (!match) {
      continue;
    }
    const rawHandle = match[1].trim();
    const suffix = match[2]?.trim().toUpperCase();
    const platform = suffix ? LEGACY_TO_PLATFORM[suffix] ?? "other" : "other";
    if (!rawHandle) {
      continue;
    }
    try {
      const link = normalizeSocialLinkInput(platform, rawHandle);
      links.push(link);
    } catch {
      // Ignore invalid entries
    }
  }

  return sortSocialLinks(links);
};

export const formatLegacySocialHandles = (links: SocialLink[]): string => {
  if (!links || links.length === 0) {
    return "";
  }

  return links
    .map((link) => {
      const metadata = SOCIAL_PLATFORM_METADATA[link.platform];
      const suffix = metadata.legacySuffix ? ` (${metadata.legacySuffix})` : "";
      const handle = link.handle.replace(/^@/, "");
      return `${handle}${suffix}`.trim();
    })
    .filter(Boolean)
    .join(", ");
};

export const sortSocialLinks = (links: SocialLink[]): SocialLink[] => {
  return [...links].sort((a, b) => {
    const aIndex = SOCIAL_PLATFORM_ORDER.indexOf(a.platform);
    const bIndex = SOCIAL_PLATFORM_ORDER.indexOf(b.platform);
    if (aIndex === bIndex) {
      return a.label?.localeCompare(b.label ?? "") ?? 0;
    }
    if (aIndex === -1) {
      return 1;
    }
    if (bIndex === -1) {
      return -1;
    }
    return aIndex - bIndex;
  });
};

export const resolveSocialLinks = (
  links?: SocialLink[] | null,
  legacy?: string | null
): SocialLink[] => {
  const normalized = normalizeSocialLinksArray(links);
  if (normalized.length > 0) {
    return normalized;
  }
  return parseLegacySocialHandles(legacy ?? "");
};

export const mergeSocialLinksWithLegacy = (
  links: SocialLink[],
  legacy?: string
): SocialLink[] => {
  const combined = [...links, ...parseLegacySocialHandles(legacy ?? "")];
  return normalizeSocialLinksArray(combined);
};

export const getPlatformLabel = (platform: SocialPlatform): string => {
  return SOCIAL_PLATFORM_METADATA[platform]?.label ?? platform;
};

